import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import {
  Snackbar,
  Alert,
  CircularProgress,
  type AlertColor,
} from "@mui/material";

interface Notification {
  id: string;
  message: string;
  severity: AlertColor;
  duration?: number;
  loading?: boolean;
}

export interface NotificationContextType {
  showNotification: (
    message: string,
    severity?: AlertColor,
    duration?: number,
  ) => string;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
  showLoading: (message: string) => string;
  updateNotification: (
    id: string,
    message: string,
    severity: AlertColor,
  ) => void;
}

export interface NotificationProviderProps {
  children: ReactNode;
  /**
   * Called once on mount with a stable `showNotification` reference.
   * Use this to wire the notification system into external sources (e.g. Apollo error links).
   */
  onMount?: (
    showNotification: (message: string, severity: AlertColor) => void,
  ) => void;
  /**
   * Renders an optional action node inside error snackbars (e.g. a feedback button).
   * Receives the error message so the action can be contextual.
   */
  renderErrorAction?: (message: string) => ReactNode;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  onMount,
  renderErrorAction,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const showNotification = useCallback(
    (
      message: string,
      severity: AlertColor = "info",
      duration: number = 6000,
    ): string => {
      const id = Math.random().toString(36).substr(2, 9);
      setNotifications((prev) => [
        ...prev,
        { id, message, severity, duration },
      ]);
      return id;
    },
    [],
  );

  useEffect(() => {
    onMount?.((message, severity) => showNotification(message, severity));
  }, [onMount, showNotification]);

  const showSuccess = useCallback(
    (message: string) => showNotification(message, "success"),
    [showNotification],
  );
  const showError = useCallback(
    (message: string) => showNotification(message, "error", 8000),
    [showNotification],
  );
  const showWarning = useCallback(
    (message: string) => showNotification(message, "warning"),
    [showNotification],
  );
  const showInfo = useCallback(
    (message: string) => showNotification(message, "info"),
    [showNotification],
  );

  const showLoading = useCallback((message: string): string => {
    const id = Math.random().toString(36).substr(2, 9);
    setNotifications((prev) => [
      ...prev,
      { id, message, severity: "info", loading: true, duration: undefined },
    ]);
    return id;
  }, []);

  const updateNotification = useCallback(
    (id: string, message: string, severity: AlertColor) => {
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id
            ? {
                ...n,
                message,
                severity,
                loading: false,
                duration: severity === "error" ? 8000 : 6000,
              }
            : n,
        ),
      );
    },
    [],
  );

  const handleClose = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  return (
    <NotificationContext.Provider
      value={{
        showNotification,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        showLoading,
        updateNotification,
      }}
    >
      {children}
      {notifications.map((notification, index) => (
        <Snackbar
          key={notification.id}
          open={true}
          autoHideDuration={
            notification.loading ? undefined : notification.duration
          }
          onClose={() => handleClose(notification.id)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          sx={{ bottom: `${24 + index * 80}px !important` }}
        >
          <Alert
            onClose={() => handleClose(notification.id)}
            severity={notification.severity}
            variant="filled"
            sx={{ width: "100%", color: "white" }}
            icon={
              notification.loading ? (
                <CircularProgress size={20} sx={{ color: "white" }} />
              ) : undefined
            }
            action={
              notification.severity === "error" && !notification.loading
                ? renderErrorAction?.(notification.message)
                : undefined
            }
          >
            {notification.message}
          </Alert>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context)
    throw new Error("useNotification must be used within NotificationProvider");
  return context;
};
